import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { fetchMerchant } from 'apis/restaurant-api/merchant-admin/merchant/api';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import useMerchantVenues from 'modules/merchant/hooks/useMerchantVenues';

import { ExperienceEntity, ExperienceEntityTree, ExperienceEntityType } from '../types';

const VENUE_PAGE_SIZE = 300;

export const useMerchant = (identifier: string, isMerchantFetchEnabled = true) => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isVenuesLoading, setIsVenuesLoading] = useState(true);
  const {
    data: merchant,
    error: merchantError,
    isLoading: isLoadingMerchant,
  } = useQuery(
    ['/v1/merchant-admin/merchants/', identifier],
    () => {
      return identifier ? fetchMerchant(identifier) : undefined;
    },
    {
      enabled: isMerchantFetchEnabled,
      refetchOnWindowFocus: false,
    },
  );

  const {
    data,
    error: merchantVenuesError,
    isLoading: isLoadingMerchantVenues,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMerchantVenues(isMerchantFetchEnabled, identifier, VENUE_PAGE_SIZE);

  useEffect(() => {
    if (isLoadingMerchantVenues || isFetchingNextPage) {
      return;
    }

    if (hasNextPage) {
      fetchNextPage();
    } else {
      setVenues(data?.pages.flatMap(page => page.venues) || []);
      setIsVenuesLoading(false);
    }
  }, [data?.pages, fetchNextPage, hasNextPage, isFetchingNextPage, isLoadingMerchantVenues]);

  const isLoading = isVenuesLoading || isLoadingMerchant || isLoadingMerchantVenues;
  const experienceEntity: ExperienceEntity | null = useMemo(() => {
    if (!merchant || isLoading) {
      return null;
    }
    return {
      experienceEntityObject: merchant,
      experienceEntityType: ExperienceEntityType.MERCHANT,
      experienceEntityId: merchant.itemid.$oid,
    };
  }, [merchant, isLoading]);

  const experienceEntityTree: ExperienceEntityTree | null = useMemo(() => {
    if (isLoading || !experienceEntity || !venues) {
      return null;
    }
    return {
      experienceEntity,
      children: venues.map(venue => ({
        experienceEntity: {
          experienceEntityObject: venue,
          experienceEntityType: ExperienceEntityType.VENUE,
          experienceEntityId: venue.id,
        },
        children: [],
      })),
    };
  }, [isLoading, experienceEntity, venues]);

  return {
    experienceEntity,
    experienceEntityTree,
    error: merchantError || merchantVenuesError,
    isLoading,
  };
};
